// Helper puri per il semaforo di review import (T-375 P1). Nessun I/O.
import { validateImportLine, type LineGateReason, type LineValidation } from '@trackeat/shared';

export interface ReviewLine {
  validation?: LineValidation;
  food_id?: string | null;
  batch_lot?: string | null;
  uom?: string | null;
  qty?: number | null;
  name?: string | null;
  category?: string | null;
  gates_ack?: boolean;
  lot_ack?: boolean;
  // T-460 — per ri-derivare i gate food-cost (costo al pezzo / unità incoerente).
  // `unit_price` e `loaded_uom` (UdM caricata dal documento, = extracted_uom) e
  // `matched_food_uom` (UdM base del food agganciato). Così applicando il
  // fattore-collo (prezzo ÷ fattore) il gate `costo_pezzo_alto` si spegne da solo.
  unit_price?: number | null;
  loaded_uom?: string | null;
  matched_food_uom?: string | null;
}

/**
 * Gate ancora APERTI su una riga: i motivi che (a) erano nella validation
 * iniziale, (b) sono ancora attivi ri-validando coi valori correnti (dopo le
 * correzioni), e (c) non sono stati confermati esplicitamente dall'operatore
 * (`lot_ack` per il lotto, `gates_ack` per gli altri). Non introduce gate nuovi
 * che l'AI non aveva sollevato — quelli dipendono da campi non ricostruibili qui
 * (es. confidenza AI per riga). Fonte unica di verità per conteggio e UI.
 */
export function lineOpenReasons(line: ReviewLine): LineGateReason[] {
  if (!line.validation || line.validation.status === 'ok') return [];
  const original = new Set(line.validation.reasons);
  const v = validateImportLine({
    matchedFoodId: line.food_id ?? null,
    matchConfidence: null,
    name: line.name ?? '',
    category: line.category ?? null,
    uom: line.uom ?? null,
    batchLot: line.batch_lot ?? null,
    qty: line.qty ?? 1,
    unitPrice: line.unit_price ?? null,
    aiLineConfidence: null,
    // T-460: UdM caricata dal documento e UdM base del food agganciato.
    loadedUom: line.loaded_uom ?? line.uom ?? null,
    matchedFoodUom: line.matched_food_uom ?? null,
  });
  // Restano solo i gate che (a) c'erano già e (b) sono ancora attivi…
  const stillActive = v.reasons.filter((r) => original.has(r));
  // …meno quelli confermati esplicitamente dall'operatore.
  return stillActive.filter((r) =>
    r === 'lotto_sospetto' ? line.lot_ack !== true : line.gates_ack !== true,
  );
}

/** Una riga è "risolta" se non ha più gate aperti. */
export function isLineResolved(line: ReviewLine): boolean {
  return lineOpenReasons(line).length === 0;
}

export function redLineCount(lines: ReviewLine[]): number {
  return lines.filter((l) => !isLineResolved(l)).length;
}
